import React, { createContext, useContext } from 'react';
import {
  getFormattingLocale,
  getLocaleFromTimezone,
  formatCurrency as formatCurrencyUtil,
  formatDateWithLocale,
  getCurrencySymbol as getCurrencySymbolUtil
} from '../utils/locale';
import { useLanguage } from './LanguageContext';

/**
 * Currency Context
 *
 * Provides currency and locale information based on user language + browser region.
 * Makes formatting functions available throughout the component tree.
 *
 * IMPORTANT: CurrencyProvider must be nested INSIDE LanguageProvider.
 */
const CurrencyContext = createContext(null);

/**
 * Currency Provider Component
 *
 * Wraps the app/booking flow and provides currency formatting utilities.
 * Locale is derived from the user's i18n language + browser region.
 *
 * @param {object} merchantData - Merchant configuration object
 * @param {string} merchantData.currency_code - ISO 4217 currency code
 * @param {string} merchantData.currency_symbol - Currency symbol (optional, auto-detected if not provided)
 */
export const CurrencyProvider = ({ merchantData, children }) => {
  const { currentLanguage } = useLanguage();

  // Derive formatting locale from user's language + browser region
  const currencyCode = merchantData?.currency_code || 'USD';
  const locale = getFormattingLocale(currentLanguage);
  const currencySymbol = merchantData?.currency_symbol || getCurrencySymbolUtil(currencyCode, locale);

  // Create formatting functions bound to this merchant's settings
  const formatCurrency = (amount) => {
    return formatCurrencyUtil(amount, currencyCode, locale);
  };

  const formatDate = (date, options = {}) => {
    return formatDateWithLocale(date, locale, options);
  };

  const getCurrencySymbol = () => {
    return currencySymbol;
  };

  const value = {
    // Currency info
    currencyCode,
    currencySymbol,
    locale,

    // Formatting functions
    formatCurrency,
    formatDate,
    getCurrencySymbol,

    // Raw merchant data for advanced use
    merchantData,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Hook to access currency context
 *
 * @returns {object} Currency context with formatting functions
 * @throws {Error} If used outside CurrencyProvider
 *
 * @example
 * const { formatCurrency, currencyCode } = useCurrency();
 * const price = formatCurrency(100); // "$100.00" or "100,00 €" depending on locale
 */
export const useCurrency = () => {
  const context = useContext(CurrencyContext);

  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }

  return context;
};

/**
 * HOC to provide currency context to a component
 * Useful for class components or when hooks are not available
 */
export const withCurrency = (Component) => {
  return (props) => {
    const currency = useCurrency();
    return <Component {...props} currency={currency} />;
  };
};

export default CurrencyContext;
